import moment from 'moment-timezone'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { timezoneSelector } from 'selectors/current_user'
import { useCurrentTime } from 'shared/contexts'

import { MeetingContainer } from './meeting_container'
import {
  shouldAddTodayPlaceholder,
  hasFutureMeetings,
  addTodayPlaceholderIfNeeded,
  addEmptyDatePlaceholderIfNeeded,
} from './utils'
import { useFilters } from '../../../hooks/meetings/filters'
import { useUpcomingPreset } from '../hooks/use_upcoming_preset'

const getFirstUpcomingEventKey = (meetings, { presetActive, timezone, currentTime }) => {
  if (!meetings || !Array.isArray(meetings)) return null
  for (const group of meetings) {
    const events = group.events || []
    for (const e of events) {
      if (e.placeholder) {
        // skip placeholders
      } else if (presetActive && e.startTime) {
        if (moment(e.startTime).tz(timezone).isAfter(currentTime)) {
          return e.key ?? e.uuid ?? null
        }
      } else if (!e.past) {
        return e.key ?? e.uuid ?? null
      }
    }
  }
  return null
}

export const MeetingList = ({
  meetings: meetingsData,
  retrieveNextPage,
  retrievePreviousPage,
  selectedMeetingId,
  hasNext,
  hasPrevious,
  showBuffers,
  isNextLoading,
  isPreviousLoading,
  contactUuid = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  onVisibleDateRangeChange,
  onScrollAnchorDateChange,
  onUserScroll,
  onDateScrollSettled,
  scrollToDateTarget = null,
  isListFetching = false,
  isListLoading = false,
}) => {
  const { getFilterParams } = useFilters()
  const { presetActive, presetPeriod } = useUpcomingPreset()
  const currentTime = useCurrentTime()
  const filterParams = getFilterParams()
  const timezone = useSelector(timezoneSelector)
  const todayStr = useMemo(() => moment().tz(timezone).format('YYYY-MM-DD'), [timezone])

  // Date-range presets (Today / This week / Last week) should not inject the legacy today
  // placeholder cards ("No upcoming meetings" + schedule CTA).
  const addTodayPlaceholder = useMemo(
    () => !presetPeriod && shouldAddTodayPlaceholder(filterParams, todayStr),
    [filterParams, todayStr, presetPeriod]
  )

  const hasFutureMeetingsValue = useMemo(
    () => hasFutureMeetings(meetingsData, todayStr),
    [meetingsData, todayStr],
  )

  const meetingsWithTodayPlaceholder = useMemo(() => addTodayPlaceholderIfNeeded(
    meetingsData,
    todayStr,
    addTodayPlaceholder,
    hasFutureMeetingsValue
  ), [addTodayPlaceholder, hasFutureMeetingsValue, meetingsData, todayStr])

  // Date-picker jump to a day with no meetings: keep surrounding days scrollable (AC6).
  const meetings = useMemo(
    () => addEmptyDatePlaceholderIfNeeded(meetingsWithTodayPlaceholder, scrollToDateTarget),
    [meetingsWithTodayPlaceholder, scrollToDateTarget]
  )

  const firstUpcomingEventKey = useMemo(
    () => getFirstUpcomingEventKey(meetings, { presetActive, timezone, currentTime }),
    [meetings, presetActive, timezone, currentTime],
  )

  return (
    <MeetingContainer
      meetings={meetings}
      onLoadNext={retrieveNextPage}
      onLoadPrevious={retrievePreviousPage}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      isNextLoading={isNextLoading}
      isPreviousLoading={isPreviousLoading}
      selectedMeetingId={selectedMeetingId}
      showBuffers={showBuffers}
      contactUuid={contactUuid}
      onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
      onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
      firstUpcomingEventKey={firstUpcomingEventKey}
      todayDate={todayStr}
      onVisibleDateRangeChange={onVisibleDateRangeChange}
      onScrollAnchorDateChange={onScrollAnchorDateChange}
      onUserScroll={onUserScroll}
      onDateScrollSettled={onDateScrollSettled}
      scrollToDateTarget={scrollToDateTarget}
      isListFetching={isListFetching}
      isListLoading={isListLoading}
    />
  )
}
